import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

const PreviewSection = ({ title, rows }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography
            variant="subtitle2"
            sx={{
                color: '#28326f',
                fontWeight: 600,
                mb: 1.5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.75rem',
            }}
        >
            {title}
        </Typography>
        <Box
            sx={{
                border: '1px solid #e8ecf4',
                borderRadius: '10px',
                overflow: 'hidden',
                backgroundColor: '#fafbfd',
            }}
        >
            {rows.map((row, index) => (
                <Box
                    key={row.label}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
                        gap: { xs: 0.5, sm: 2 },
                        px: 2,
                        py: 1.5,
                        borderBottom: index < rows.length - 1 ? '1px solid #eef1f7' : 'none',
                        backgroundColor: '#fff',
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        {row.label}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#1f2937', fontWeight: 500, wordBreak: 'break-word' }}
                    >
                        {row.value || '-'}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
    if (!value) return '-';
    return moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', 'YYYY-MM-DD HH:mm:ss']).format('DD-MM-YYYY');
};

const getApprovalTypeLabel = (paymentType) => {
    if (paymentType === '1') return 'FULL PAYMENT';
    if (paymentType === '2') return 'PART PAYMENT';
    return '-';
};

const ApprovalRoyaltyPreview = ({
    showPreview,
    setShowPreview,
    formData,
    handleSubmit,
    invoiceList,
    authorities,
    authorityAccounts,
    approvalAction,
}) => {
    const handleClose = () => setShowPreview(false);

    const authorityLabel = authorities.find(
        (item) => String(item.id) === String(formData.authority_id)
    )?.name || '-';

    const account = authorityAccounts.find(
        (item) => String(item.id) === String(formData.authority_account_id)
    );
    const accountLabel = account ? `${account.bank_name} [${account.account_no}]` : '-';

    const paymentRows = [
        { label: 'Payment Order No', value: formData.payment_order_no },
        { label: 'Payment Order Date', value: formatDate(formData.payment_order_date) },
        { label: 'Authority', value: authorityLabel },
        { label: 'Authority Account', value: accountLabel },
        { label: 'Challan Value', value: formatAmount(formData.amount) },
    ];

    if (formData.l2_remarks) {
        paymentRows.push({ label: 'L2 Remarks', value: formData.l2_remarks });
    }
    if (formData.l3_remarks) {
        paymentRows.push({ label: 'L3 Remarks', value: formData.l3_remarks });
    }

    const isApprove = String(approvalAction) === '1';
    const isReject = String(approvalAction) === '0';

    return (
        <Dialog
            open={showPreview}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    color: '#28326f',
                    fontWeight: 600,
                }}
            >
                {isApprove ? 'Confirm Royalty Approval' : isReject ? 'Confirm Royalty Rejection' : 'Review Royalty Details'}
                <IconButton aria-label="close" onClick={handleClose} size="small" sx={{ color: '#6b7280' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 2.5 }}>
                    {isApprove
                        ? 'Please review the details below before approving this royalty challan.'
                        : isReject
                            ? 'Please review the details below before rejecting this royalty challan.'
                            : 'Please verify the information below before submitting.'}
                </Typography>

                <PreviewSection title="Payment Information" rows={paymentRows} />

                {invoiceList?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                color: '#28326f',
                                fontWeight: 600,
                                mb: 1.5,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                fontSize: '0.75rem',
                            }}
                        >
                            Invoice Details ({invoiceList.length})
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {invoiceList.map((invoice, index) => {
                                const invoiceRows = [
                                    { label: 'Invoice No', value: invoice.invoice_no },
                                    { label: 'Invoice Date', value: formatDate(invoice.invoice_date) },
                                    { label: 'Beneficiary', value: invoice.company_name },
                                    { label: 'Approval Type', value: getApprovalTypeLabel(invoice.payment_type) },
                                    { label: 'Voucher No', value: invoice.voucher_no },
                                    { label: 'Voucher Date', value: formatDate(invoice.voucher_date) },
                                    { label: 'Taxable Amount', value: formatAmount(invoice.taxable_amount) },
                                    { label: 'Invoice Amount', value: formatAmount(invoice.payable_amount) },
                                    { label: 'Sanctioned Amount', value: formatAmount(invoice.sanction_amount) },
                                    { label: 'Royalty Amount', value: formatAmount(invoice.royalty_amount) },
                                ];

                                return (
                                    <Box
                                        key={`invoice_preview_${invoice.id || invoice.invoice_no || index}`}
                                        sx={{
                                            border: '1px solid #e8ecf4',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                backgroundColor: '#f3f5fb',
                                                borderBottom: '1px solid #e8ecf4',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#28326f' }}>
                                                Invoice {index + 1}
                                                {invoice.invoice_no ? ` — ${invoice.invoice_no}` : ''}
                                            </Typography>
                                        </Box>
                                        {invoiceRows.map((row, rowIndex) => (
                                            <Box
                                                key={row.label}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                                                    gap: { xs: 0.5, sm: 2 },
                                                    px: 2,
                                                    py: 1.25,
                                                    borderBottom: rowIndex < invoiceRows.length - 1 ? '1px solid #eef1f7' : 'none',
                                                    backgroundColor: '#fff',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                                                    {row.label}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 500 }}>
                                                    {row.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none',
                        borderColor: '#d1d5db',
                        color: '#374151',
                    }}
                >
                    Change
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: isReject ? '#dc2626' : '#28326f',
                        '&:hover': {
                            backgroundColor: isReject ? '#b91c1c' : '#1f2659',
                        },
                    }}
                >
                    {isApprove ? 'Confirm Approval' : isReject ? 'Confirm Rejection' : 'Confirm & Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApprovalRoyaltyPreview;
